/**
 * ResetPasswordTemplate - Reset Password Page Template
 *
 * Pure presentation component that composes the reset password page.
 * Follows the Template + Wrapper pattern used for Login and Register pages.
 *
 * Architecture:
 * - Pure presentation (no business logic)
 * - Composes: Page title + ResetPasswordForm + navigation links
 * - Used within AuthLayoutTemplate for two-column layout
 *
 * Features:
 * - Page title and description
 * - Reset password form with password strength
 * - Link back to login
 *
 * Usage:
 * ```tsx
 * <AuthLayoutTemplate isWeb={true}>
 *   <ResetPasswordTemplate
 *     onSubmit={handleResetPassword}
 *     onNavigateToLogin={handleNavigateToLogin}
 *     isLoading={isLoading}
 *     isWeb={true}
 *     token={resetToken}
 *   />
 * </AuthLayoutTemplate>
 * ```
 */

import React from 'react';
import { ResetPasswordForm } from '../molecules/ResetPasswordForm';

export interface ResetPasswordTemplateProps {
    /** Callback when form is submitted with new password */
    onSubmit: (password: string) => Promise<void>;

    /** Callback to navigate to login page */
    onNavigateToLogin?: () => void;

    /** Loading state during submission */
    isLoading?: boolean;

    /** Platform flag for styling (web vs plugin) */
    isWeb?: boolean;

    /** Initial error message to display */
    initialError?: string;

    /** Reset token from URL */
    token?: string;
}

/**
 * ResetPasswordTemplate
 *
 * Template component for the reset password page.
 * Provides the complete UI for setting a new password.
 */
export const ResetPasswordTemplate: React.FC<ResetPasswordTemplateProps> = ({
    onSubmit,
    onNavigateToLogin,
    isLoading = false,
    isWeb = true,
    initialError,
    token,
}) => {
    return (
        <div className="w-full max-w-md mx-auto px-6 py-8">
            {/* Page Title */}
            <div className="mb-8 text-center">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Reset Your Password
                </h1>
                <p className="text-sm text-gray-600">
                    Enter your new password below. Make sure it's strong and secure.
                </p>
            </div>

            {/* Reset Password Form */}
            <ResetPasswordForm
                onSubmit={onSubmit}
                isLoading={isLoading}
                isWeb={isWeb}
                initialError={initialError}
                onBackToLogin={onNavigateToLogin}
                token={token}
            />

            {/* Additional Help Text */}
            <div className="mt-6 text-center">
                <p className="text-xs text-gray-500">
                    Remember your password?{' '}
                    {onNavigateToLogin && (
                        <button
                            type="button"
                            onClick={onNavigateToLogin}
                            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
                        >
                            Sign in
                        </button>
                    )}
                </p>
            </div>
        </div>
    );
};
